import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "../store";
import { setCreateMode } from "../reducers/tableManagementReducer";
import { employeesApi } from "../../api/employeesApi";
import { StatusTypes } from "../../types/common";
import { PaginationParameters } from "../../api/Pagination";
import { EmployeeUserType } from "../../types/employeeType";
import { handleRequest } from "../utils/handleRequest";
import i18n from "../../i18n";

export const fetchPaginatedEmployeesThunk = createAsyncThunk<
  { items: EmployeeUserType[]; totalCount: number } | null,
    { paginationParams: PaginationParameters, groupIds: number[] },
  { dispatch: AppDispatch; state: RootState }
>("employees/fetchPaginatedEmployees", async (params, { dispatch }) => {
  const callResult = await handleRequest(
    dispatch,
    "indirect",
    () => employeesApi.getAccountManagementEmployees(params.paginationParams, params.groupIds)
  );
  if (!callResult.success() || !callResult.result) return null;
  const transformedItems: EmployeeUserType[] = callResult.result.items.map((item: any) => ({
    ...item,
    groupIds: Array.isArray(item.groups) ? item.groups.map(g => g.id) : [],
  }));

  return { ...callResult.result, items: transformedItems };
});

export const fetchPaginatedEmployeesByIdThunk = createAsyncThunk<
  { items: EmployeeUserType[]; totalCount: number; totalIds: string[] } | null,
  { paginationParams: PaginationParameters, employeeIds: string[]},
  { dispatch: AppDispatch; state: RootState }
>("employees/fetchPaginatedEmployeesById", async (params, { dispatch }) => {

  const callResult = await handleRequest(
    dispatch,
    "indirect",
    () => employeesApi.getAccountManagementEmployeesByIds(params.paginationParams, params.employeeIds)
  );

  if (!callResult.success() || !callResult.result) {
    return null;
  }

  const transformedItems: EmployeeUserType[] = callResult.result.items.map((item: any) => ({
    ...item,
  }));

  return {
    items: transformedItems,
    totalCount: callResult.result.totalCount,
    totalIds: callResult.result.totalIds
  };
});

export const fetchAllEmployeesThunk = createAsyncThunk<EmployeeUserType[] | null, void, { dispatch: AppDispatch; state: RootState }>(
  "employees/fetchAllEmployees",
  async (_, { dispatch }) => {
    const callResult = await handleRequest(dispatch, "indirect", () => employeesApi.getCompanyEmployees());
    return callResult.success() && callResult.result ? callResult.result : null;
  }
);

export const fetchEmployeeByIdThunk = createAsyncThunk<
  EmployeeUserType | null,
  string,
  { dispatch: AppDispatch; state: RootState }
>(
  "employees/fetchEmployeeById",
  async (employeeId, { dispatch }) => {
    const callResult = await handleRequest(
      dispatch,
      "indirect",
      () => employeesApi.getEmployeeById(employeeId)
    );

    if (!callResult.success() || !callResult.result) {
      return null;
    }

    const employee = callResult.result;
    const formattedEmployee: EmployeeUserType = {
      ...employee,
      groupIds: Array.isArray(employee.groups)
        ? employee.groups.map((g: any) => g.id)
        : [],
      walletId: employee.walletId,
      actionModules: employee.actionModules,
      shopModules: employee.shopModules,
      serviceModules: employee.serviceModules,
    };

    return formattedEmployee;
  }
);

export const createNewEmployeeThunk = createAsyncThunk<EmployeeUserType | null, Partial<EmployeeUserType>, { dispatch: AppDispatch; state: RootState }>(
  "employees/createUser",
  async (user, { dispatch }) => {
    const newUser = {
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      birthDate: user.birthDate ?? null,
      employmentDate: user.employmentDate ?? null,
      employeeNumber: user.employeeNumber ?? "",
      position: user.position ?? "",
      status: user.status || StatusTypes.Blocked,
      groupIds: user.groupIds ?? [],
      tachographNumber: "",
      subCompanyId: user.subCompanyId ?? null,
      subCompanyName: user.subCompanyName ?? null,
    };

    const callResult = await handleRequest(
      dispatch,
      "direct",
      () => employeesApi.createEmployee([newUser]),
      i18n.t("requestMessage.employeeCreated")
    );
    if (callResult.success()) {
      dispatch(setCreateMode(false));
    }
    return callResult.success() ? callResult.result?.[0] ?? null : null;
  }
);

export const editEmployeeThunk = createAsyncThunk<
  EmployeeUserType,
  EmployeeUserType,
  { dispatch: AppDispatch }
>(
  "employees/editUser",
  async (user, { dispatch }) => {
    const callResult = await handleRequest(
      dispatch,
      "direct",
      () =>
        employeesApi.editEmployee(user.id, {
          firstName: user.firstName,
          lastName: user.lastName,
          birthDate: user.birthDate,
          employmentDate: user.employmentDate,
          position: user.position,
          employeeNumber: user.employeeNumber,
          tachographNumber: user.tachographNumber,
          status: user.status,
          groupIds: user.groupIds ?? [],
          subCompanyId: user.subCompanyId,
        }),
      i18n.t("requestMessage.employeeUpdated")
    );

    if (!callResult.success() || !callResult.result) {
      throw new Error("Failed to update employee");
    }

    return callResult.result;
  }
);

export const deleteEmployeeThunk = createAsyncThunk<string | null, string, { dispatch: AppDispatch; state: RootState }>(
  "employees/deleteUser",
  async (userId, { dispatch }) => {
    const callResult = await handleRequest(
      dispatch,
      "direct",
      () => employeesApi.deleteEmployee(userId),
      i18n.t("requestMessage.employeeDeleted")
    );
    if (callResult.success()) {
      dispatch(setCreateMode(false));
      return userId;
    }
    return null;
  }
);

export const deleteEmployeePasswordThunk = createAsyncThunk<
  string | null,
  string,
  { dispatch: AppDispatch; state: RootState }
>(
  "employees/deletePassword",
  async (employeeId, { dispatch }) => {
    const callResult = await handleRequest(
      dispatch,
      "direct",
      () => employeesApi.deleteEmployeePassword(employeeId),
      i18n.t("requestMessage.employeePasswordReset")
    );

    if (callResult.success()) {
      return employeeId;
    }
    return null;
  }
);

export const uploadEmployeesThunk = createAsyncThunk<EmployeeUserType[] | null, void, { dispatch: AppDispatch; state: RootState }>(
  "employees/uploadEmployees",
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const excelUploadEmployeesParsedData = state.employees.excelUploadEmployeesParsedData;

    const callResult = await handleRequest(
      dispatch,
      "direct",
      () => employeesApi.createEmployee(excelUploadEmployeesParsedData ?? []),
      i18n.t("requestMessage.employeesUploaded")
    );
    return callResult.success() && callResult.result ? callResult.result : null;
  }
);

export const fetchEmployeeServiceModulesThunk = createAsyncThunk<{ employeeId: string; moduleIds: number[] } | null, string, { dispatch: AppDispatch; state: RootState }>(
  "employees/fetchEmployeeServiceModules",
  async (employeeId, { dispatch }) => {
    const callResult = await handleRequest(dispatch, "indirect", () => employeesApi.getEmployeeServiceModules(employeeId));

    if (!callResult.success() || !callResult.result) return null;
    return { employeeId, moduleIds: callResult.result ?? [] };
  }
);

export const patchEmployeeServiceModulesThunk = createAsyncThunk<{ employeeId: string; moduleIds: number[] } | null, { employeeId: string; moduleIds: number[] }, { dispatch: AppDispatch; state: RootState }>(
  "employees/patchEmployeeServiceModules",
  async ({ employeeId, moduleIds }, { dispatch }) => {
    const callResult = await handleRequest(
      dispatch,
      "direct",
      () => employeesApi.patchEmployeeServiceModules(employeeId, moduleIds),
      i18n.t("requestMessage.serviceModulesUpdated")
    );
    if (!callResult.success()) return null;
    return { employeeId, moduleIds };
  }
);

export const fetchEmployeeActionModulesThunk = createAsyncThunk<{ employeeId: string; moduleIds: number[] } | null, string, { dispatch: AppDispatch; state: RootState }>(
  "employees/fetchEmployeeActionModules",
  async (employeeId, { dispatch }) => {
    const callResult = await handleRequest(dispatch, "indirect", () => employeesApi.getEmployeeActionModules(employeeId));
    if (!callResult.success() || !callResult.result) return null;
    return { employeeId, moduleIds: callResult.result ?? [] };
  }
);

export const patchEmployeeActionModulesThunk = createAsyncThunk<{ employeeId: string; moduleIds: number[] } | null, { employeeId: string; moduleIds: number[] }, { dispatch: AppDispatch; state: RootState }>(
  "employees/patchEmployeeActionModules",
  async ({ employeeId, moduleIds }, { dispatch }) => {
    const callResult = await handleRequest(
      dispatch,
      "direct",
      () => employeesApi.patchEmployeeActionModules(employeeId, moduleIds),
      i18n.t("requestMessage.actionModulesUpdated")
    );
    if (!callResult.success()) return null;
    return { employeeId, moduleIds };
  }
);

export const fetchEmployeeShopModulesThunk = createAsyncThunk<{ employeeId: string; moduleIds: number[] } | null, string, { dispatch: AppDispatch; state: RootState }>(
  "employees/fetchEmployeeShopModules",
  async (employeeId, { dispatch }) => {
    const callResult = await handleRequest(dispatch, "indirect", () => employeesApi.getEmployeeShopModules(employeeId));
    if (!callResult.success() || !callResult.result) return null;
    return { employeeId, moduleIds: callResult.result ?? [] };
  }
);

export const patchEmployeeShopModulesThunk = createAsyncThunk<{ employeeId: string; moduleIds: number[] } | null, { employeeId: string; moduleIds: number[] }, { dispatch: AppDispatch; state: RootState }>(
  "employees/patchEmployeeShopModules",
  async ({ employeeId, moduleIds }, { dispatch }) => {
    const callResult = await handleRequest(
      dispatch,
      "direct",
      () => employeesApi.patchEmployeeShopModules(employeeId, moduleIds),
      i18n.t("requestMessage.shopModulesUpdated")
    );
    if (!callResult.success()) return null;
    return { employeeId, moduleIds };
  }
);

export const bookTransactionForEmployeeThunk = createAsyncThunk<
  void,
  { employeeId: string },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "transactions/bookTransactionForEmployee",
  async ({ employeeId }, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<void>(
      dispatch,
      "direct",
      () => employeesApi.bookTransactionForEmployee(employeeId),
      i18n.t("requestMessage.transactionBooked")
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to book transaction");
    }
    return res.result as void;
  }
);